import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Search, FileDown, Printer } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const CreditNotes = () => {
  const [notes, setNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    party_type: "customer",
    party_id: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notesRes, custRes, suppRes] = await Promise.all([
        axios.get(`${API}/credit-notes`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/suppliers`),
      ]);
      setNotes(notesRes.data);
      setCustomers(custRes.data);
      setSuppliers(suppRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.party_id || !formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Cari ve tutar zorunludur");
      return;
    }

    try {
      await axios.post(`${API}/credit-notes`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success("Alacak dekontu kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu alacak dekontunu silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/credit-notes/${id}`);
      toast.success("Alacak dekontu silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      party_type: "customer",
      party_id: "",
      date: new Date().toISOString().split("T")[0],
      amount: "",
      description: "",
    });
  };

  const getPartyOptions = () => {
    if (formData.party_type === "customer") {
      return customers.map((c) => ({
        value: c.id,
        label: c.name,
        subtitle: `Bakiye: ₺${(c.balance || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
      }));
    }
    return suppliers.map((s) => ({
      value: s.id,
      label: s.name,
      subtitle: `Bakiye: ₺${(s.balance || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
    }));
  };

  const filteredNotes = notes.filter(
    (n) =>
      n.party_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.document_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePagePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4 md:space-y-6 print:space-y-4" data-testid="credit-notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Alacak Dekontu
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">
            Müşteri borcunu azaltır, tedarikçi borcumuzu artırır
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePagePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-credit-note-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Alacak Dekontu
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4 sm:mx-auto">
              <DialogHeader>
                <DialogTitle>Yeni Alacak Dekontu</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Cari Türü *</Label>
                  <Select
                    value={formData.party_type}
                    onValueChange={(value) => setFormData({ ...formData, party_type: value, party_id: "" })}
                  >
                    <SelectTrigger data-testid="credit-note-party-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Müşteri</SelectItem>
                      <SelectItem value="supplier">Tedarikçi</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.party_type === "customer" 
                      ? "Müşterinin bize olan borcu azalır" 
                      : "Tedarikçiye olan borcumuz artar"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{formData.party_type === "customer" ? "Müşteri" : "Tedarikçi"} *</Label>
                  <SearchableSelect
                    options={getPartyOptions()}
                    value={formData.party_id}
                    onChange={(value) => setFormData({ ...formData, party_id: value })}
                    placeholder={formData.party_type === "customer" ? "Müşteri seçin" : "Tedarikçi seçin"}
                    searchPlaceholder="Ara..."
                    emptyMessage="Sonuç bulunamadı."
                    data-testid="credit-note-party-select"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tarih</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      data-testid="credit-note-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tutar *</Label>
                    <CurrencyInput
                      value={formData.amount}
                      onChange={(val) => setFormData({ ...formData, amount: val })}
                      placeholder="Tutar girin"
                      data-testid="credit-note-amount"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Dekont açıklaması"
                    rows={2}
                    data-testid="credit-note-description"
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                    İptal
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="credit-note-save-btn">
                    Kaydet
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 md:pb-4 p-4 md:p-6">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Alacak dekontu ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="credit-note-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredNotes.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredNotes.map((note) => (
                  <div key={note.id} className="p-4 space-y-3" data-testid={`credit-note-row-${note.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{note.party_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{note.document_no}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(note.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">{note.date}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          note.party_type === "customer" 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          {note.party_type === "customer" ? "Müşteri" : "Tedarikçi"}
                        </span>
                      </div>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        ₺{note.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {note.description && (
                      <p className="text-sm text-slate-500 truncate">{note.description}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Belge No</TableHead>
                      <TableHead className="font-semibold">Cari Türü</TableHead>
                      <TableHead className="font-semibold">Cari Adı</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold">Açıklama</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNotes.map((note) => (
                      <TableRow key={note.id}>
                        <TableCell className="font-mono text-sm">{note.document_no}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            note.party_type === "customer" 
                              ? "bg-blue-100 text-blue-700" 
                              : "bg-orange-100 text-orange-700"
                          }`}>
                            {note.party_type === "customer" ? "Müşteri" : "Tedarikçi"}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{note.party_name}</TableCell>
                        <TableCell>{note.date}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-500">
                          {note.description || "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-emerald-600">
                          ₺{note.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(note.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FileDown className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz alacak dekontu yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditNotes;
